import PropTypes from "prop-types";
import React, { Component } from "react";
import { withApollo } from "react-apollo";

import {
  CREATE_SUBSECTION,
  DELETE_SUBSECTION,
  UPDATE_SUBSECTION,
} from "./../../gql";

import ContentPanel from "./../../components/ContentPanel";
import ErrorBoundary from "./../../components/ErrorBoundary";
import Tags from "./../../components/Tags";
import Throbber from "./../../components/Throbber";
import VisibilityToggle from "./../../components/VisibilityToggle";
import WysiwygEditor from "./../../components/WysiwygEditor";
import { addToChildList, removeFromChildList } from "./../../utils/cache";

import roundPlus from "./../../../images/icons/round-plus-white.svg";

class Subsections extends Component {
  constructor() {
    super();
    this.state = {
      subsectionUpdate: {},
      publishIsUpToDate: null,
      isCreating: false,
      createError: null,
      deletingId: null,
      deleteError: null,
    };
    this.submitDelay = 500;
  }

  componentDidUpdate(prevProps) {
    const { published } = this.props;

    if (!published && prevProps.published) {
      this.setState({
        publishIsUpToDate: false,
      });
    }

    if (published && !prevProps.published) {
      this.setState({
        publishIsUpToDate: true,
      });
    }
  }

  parentList() {
    const { sectionId } = this.props;
    return {
      parentType: "Section",
      parentId: sectionId,
      field: "subsections",
    };
  }

  createSubsection(subsections) {
    const {
      sectionId,
      app,
      client,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    const orderIndex =
      subsections.length > 0
        ? subsections[subsections.length - 1].meta.displayOrder + 1
        : 0;

    this.setState({ isCreating: true, createError: null });
    client
      .mutate({
        mutation: CREATE_SUBSECTION,
        variables: {
          title: "",
          subtitle: "",
          sectionId: sectionId,
          languageCode: languageCode,
          app: app,
          content: {
            document: {
              nodes: [
                {
                  object: "block",
                  type: "paragraph",
                },
              ],
            },
          },
          hiddenFromDashboard: false,
          shownRandomly: true,
          displayOrder: orderIndex,
        },
        update: (cache, { data }) =>
          addToChildList(cache, this.parentList(), data.createSubsection),
      })
      .then(() => this.setState({ isCreating: false }))
      .catch((error) =>
        this.setState({
          isCreating: false,
          createError: error.message,
        }),
      );
    activatePublishButton();
    activateUnpublishedAlert();
  }

  deleteSubsection(id) {
    const { client, activatePublishButton, activateUnpublishedAlert } =
      this.props;
    if (confirm("Are you sure you want to delete this subsection?")) {
      this.setState({ deletingId: id, deleteError: null });
      client
        .mutate({
          mutation: DELETE_SUBSECTION,
          variables: { id },
          update: (cache) =>
            removeFromChildList(cache, this.parentList(), id),
        })
        .then(() => this.setState({ deletingId: null }))
        .catch((error) =>
          this.setState({
            deletingId: null,
            deleteError: { id, message: error.message },
          }),
        );
      activatePublishButton();
      activateUnpublishedAlert();
    }
  }

  updateSubsection(variables) {
    const { client } = this.props;

    // the mutation returns the updated subsection, which Apollo merges into the cache
    client
      .mutate({
        mutation: UPDATE_SUBSECTION,
        variables,
      })
      .catch(() => {
        // errors are logged by the errorLink
      });
  }

  submitUpdate() {
    const { activatePublishButton, activateUnpublishedAlert } = this.props;
    const { subsectionUpdate } = this.state;
    this.updateSubsection(subsectionUpdate);
    this.setState({
      subsectionUpdate: {},
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onIsBornChange(id, value) {
    const {
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;
    const isBorn = value ? false : true;

    this.updateSubsection({
      app,
      languageCode,
      id,
      isBorn,
      published: false,
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onChangeHandle(event, id) {
    const { app, languageCode, published } = this.props;
    const name = event.target.name;
    const value = isNaN(event.target.value)
      ? event.target.value
      : Number(event.target.value);

    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(() => this.submitUpdate(), this.submitDelay);

    this.setState((prevState) => {
      if (prevState.subsectionUpdate && prevState.subsectionUpdate.id === id) {
        return {
          subsectionUpdate: {
            ...this.state.subsectionUpdate,
            id: id,
            [name]: value,
          },
        };
      }
      return {
        subsectionUpdate: {
          id: id,
          app: app,
          published: published,
          languageCode: languageCode,
          [name]: value,
        },
      };
    });
  }

  onChangeEditor(value, id) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    clearTimeout(this.editorTimer);
    this.editorTimer = setTimeout(() => {
      this.updateSubsection({
        id: id,
        app: app,
        published: published,
        languageCode: languageCode,
        content: value,
      });
      activatePublishButton();
      activateUnpublishedAlert();
    }, this.submitDelay);
  }

  onVisibilityChange(event, id) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;
    this.updateSubsection({
      id: id,
      app: app,
      published: published,
      languageCode: languageCode,
      hiddenFromDashboard: event === "hiddenFromDashboard" ? true : false,
      shownRandomly: event === "hiddenFromDashboard" ? false : true,
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  onTagsChange(tags, id) {
    const {
      app,
      languageCode,
      published,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    this.updateSubsection({
      id: id,
      app: app,
      published: published,
      languageCode: languageCode,
      tags: tags,
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  updateDisplayOrder(id, order) {
    const {
      app,
      languageCode,
      activatePublishButton,
      activateUnpublishedAlert,
    } = this.props;

    this.updateSubsection({
      id,
      app,
      languageCode,
      published: false,
      displayOrder: order,
    });
    activatePublishButton();
    activateUnpublishedAlert();
  }

  publishNotification() {
    const { activatePublishButton } = this.props;
    const { publishIsUpToDate } = this.state;

    if (publishIsUpToDate === null) {
      activatePublishButton();
    } else if (publishIsUpToDate) {
      return;
    }

    return (
      <div className="notification notification--warning">
        <p className="notification__description">
          The content below has not been published yet.
        </p>
      </div>
    );
  }

  render() {
    const { subsections } = this.props;
    const { app, languageCode, published } = this.props;
    const { isCreating, createError, deletingId, deleteError } = this.state;

    return (
      <div>
        {subsections
          .sort((a, b) => a.meta.displayOrder - b.meta.displayOrder)
          .map((subsection) => (
            <div key={subsection.id} className="relative-block">
              {deleteError && deleteError.id === subsection.id && (
                <p className="full-width center-align">{deleteError.message}</p>
              )}
              {!subsection.availability.published &&
                this.publishNotification()}
              <ContentPanel
                key={subsection.id}
                enableWeekRange
                enableOrdering
                id={subsection.id}
                title={subsection.title}
                isBorn={subsection.availability.isBorn}
                rangeFrom={subsection.availability.fromWeek}
                rangeTo={subsection.availability.toWeek}
                displayOrder={subsection.meta.displayOrder}
                siblingPanels={subsections}
                onIsBornChange={(id, value) => this.onIsBornChange(id, value)}
                updateDisplayOrder={(id, order) =>
                  this.updateDisplayOrder(id, order)
                }
                onChanges={(event) => this.onChangeHandle(event, subsection.id)}
                deletePanel={() => this.deleteSubsection(subsection.id)}
              >
                <ErrorBoundary>
                  <WysiwygEditor
                    enableArticleLinking
                    enableExternalLinking
                    enableImageUpload
                    enableVimeoEmbed
                    enableEmbeds
                    languageCode={languageCode}
                    app={app}
                    published={published}
                    editorData={subsection.content}
                    onChangeValue={(event) =>
                      this.onChangeEditor(event, subsection.id)
                    }
                  />
                </ErrorBoundary>
                <div className="content-panel__footer">
                  <div className="content-panel__form">
                    <Tags
                      tags={subsection.tags}
                      onChange={(event) =>
                        this.onTagsChange(event, subsection.id)
                      }
                    />
                  </div>
                  <div className="content-panel__actions">
                    <VisibilityToggle
                      parentId={subsection.id}
                      onChange={(event) =>
                        this.onVisibilityChange(event, subsection.id)
                      }
                      options={[
                        {
                          id: "shownRandomly",
                          checked: subsection.availability.shownRandomly,
                        },
                        {
                          id: "hiddenFromDashboard",
                          checked: subsection.availability.hiddenFromDashboard,
                        },
                      ]}
                    />
                  </div>
                </div>
              </ContentPanel>
              <Throbber
                hidden={deletingId !== subsection.id}
                loadingText="Deleting section..."
              />
            </div>
          ))}
        <div className="full-width right-align relative-block">
          <Throbber hidden={!isCreating} loadingText="Creating subsection..." />
          {createError && (
            <p className="full-width center-align">{createError}</p>
          )}
          <div className="full-width right-align">
            <button
              className="button button--small button--icon-text"
              disabled={isCreating}
              onClick={() => this.createSubsection(subsections)}
            >
              <img
                src={roundPlus}
                alt="Add new subsection"
                className="button__icon"
              />
              Add new subsection
            </button>
          </div>
        </div>
      </div>
    );
  }
}

Subsections.propTypes = {
  sectionId: PropTypes.string.isRequired,
  subsections: PropTypes.array.isRequired,
  languageCode: PropTypes.string.isRequired,
  published: PropTypes.bool.isRequired,
  app: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired,
  activatePublishButton: PropTypes.func.isRequired,
  activateUnpublishedAlert: PropTypes.func.isRequired,
};

export default withApollo(Subsections);
